/**
 * HomeTab - Dashboard overview (Acasă) 
 * Extracted from MasterDashboard.jsx
 * Shows: today's attendance, stats cards, tool usage, DashboardStats
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Users, MapPin, Wrench, Trash2, Edit2, ExternalLink } from "lucide-react";
import DashboardStats from "@/components/dashboard/DashboardStats";
import ProjectPipelineTimeline from "@/components/dashboard/ProjectPipelineTimeline";
import { HelpTooltip } from "@/components/HelpTooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
  } catch (error) { return iso; }
};

export default function HomeTab({ companyId, sites, tools, workers, onEditAttendance, onDeleteAttendance }) {
  const [todayAttendance, setTodayAttendance] = useState([]);

  const fetchTodayAttendance = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/attendance/today`, { params: { company_id: companyId } });
      setTodayAttendance(response.data);
    } catch (error) {
      console.error("Error fetching attendance:", error);
    }
  }, [companyId]);

  useEffect(() => {
    fetchTodayAttendance();
    const interval = setInterval(fetchTodayAttendance, 30000);
    return () => clearInterval(interval);
  }, [fetchTodayAttendance]);

  const handleRunAutoAttendance = async () => {
    try {
      const response = await axios.post(`${API}/admin/auto-attendance`, null, { params: { company_id: companyId } });
      const data = response.data;
      if (data.created > 0) {
        toast.success(data.message);
      } else {
        toast.info(data.message || "Toți angajații au deja pontaj pentru astăzi");
      }
      fetchTodayAttendance();
    } catch (error) {
      toast.error("Eroare la generarea pontajului automat");
    }
  };

  const openGoogleMaps = (lat, lng) => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <h2 className="text-2xl font-bold" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Prezențe Astăzi</h2>
          <HelpTooltip section="acasa" />
        </div>
        <Button onClick={handleRunAutoAttendance} className="glass-stat-card bg-gradient-to-r from-teal-400 to-blue-500 text-white border-0 shadow-md hover:shadow-lg rounded-xl">
          Generează Pontaj Automat
        </Button>
      </div>
      
      <Card className="glass-card-lift border-0 shadow-lg rounded-2xl" data-testid="attendance-card">
        <CardContent className="pt-6">
          {todayAttendance.length === 0 ? (
            <p className="text-slate-500 text-center py-8">Nicio prezență înregistrată astăzi</p>
          ) : (
            <div className="space-y-3">
              {todayAttendance.map((att) => (
                <div key={att.id} className={`glass-row-hover flex items-center justify-between p-4 rounded-xl border ${att.is_auto_generated ? 'bg-orange-50/70 border-orange-200' : 'bg-white/40 backdrop-blur-sm border-white/50'}`} data-testid={`attendance-item-${att.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-lg">{att.user_name}</p>
                      {att.is_auto_generated && <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300">AUTO</Badge>}
                      {att.edited_by_worker && <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">EDITAT</Badge>}
                    </div>
                    <div className="flex items-center gap-4 mt-2">
                      <span className="text-sm text-slate-600"><MapPin className="w-3 h-3 inline mr-1" />{att.site_name}</span>
                      {att.location && (
                        <button onClick={() => openGoogleMaps(att.location.latitude, att.location.longitude)} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" />Vezi Locație GPS
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {att.tool_names?.map((tool) => (
                        <span key={tool} className="px-2 py-1 bg-teal-100 text-teal-700 text-xs rounded-full">{tool}</span>
                      ))}
                    </div>
                  </div>
                  <div className="text-right flex items-center gap-2">
                    <div>
                      <p className="text-sm text-slate-500">PREZENT</p>
                      <p className="font-mono font-semibold text-lg">{formatTime(att.check_in)}</p>
                      {att.check_out && (
                        <>
                          <p className="text-sm text-slate-500 mt-2">ÎNCHEIERE</p>
                          <p className="font-mono font-semibold">{formatTime(att.check_out)}</p>
                          <p className="text-xs text-slate-600 mt-1">{att.hours_worked?.toFixed(1)} ore</p>
                        </>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button variant="outline" size="sm" onClick={() => onEditAttendance?.(att)} className="text-blue-600 hover:text-blue-800" data-testid={`edit-attendance-${att.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => onDeleteAttendance?.(att.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50" data-testid={`delete-attendance-${att.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="glass-stat-card border-0 shadow-lg bg-gradient-to-br from-teal-400 via-teal-500 to-blue-600 rounded-2xl">
          <CardContent className="pt-6 relative z-10">
            <div className="flex items-center justify-between">
              <div><p className="stat-label text-sm font-medium">Prezențe Astăzi</p><p className="text-4xl font-bold mt-1 drop-shadow-sm">{todayAttendance.length}</p></div>
              <Users className="stat-icon w-12 h-12" />
            </div>
          </CardContent>
        </Card>
        <Card className="glass-stat-card border-0 shadow-lg bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-600 rounded-2xl">
          <CardContent className="pt-6 relative z-10">
            <div className="flex items-center justify-between">
              <div><p className="stat-label text-sm font-medium">Total Proiecte</p><p className="text-4xl font-bold mt-1 drop-shadow-sm">{sites.length}</p></div>
              <MapPin className="stat-icon w-12 h-12" />
            </div>
          </CardContent>
        </Card>
        <Card className="glass-stat-card border-0 shadow-lg bg-gradient-to-br from-violet-400 via-indigo-500 to-purple-600 rounded-2xl">
          <CardContent className="pt-6 relative z-10">
            <div className="flex items-center justify-between">
              <div><p className="stat-label text-sm font-medium">Total Unelte</p><p className="text-4xl font-bold mt-1 drop-shadow-sm">{tools.length}</p></div>
              <Wrench className="stat-icon w-12 h-12" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tool Usage Stats */}
      <Card className="glass-card-lift border-0 shadow-lg rounded-2xl">
        <CardHeader>
          <CardTitle style={{fontFamily: 'Space Grotesk, sans-serif'}}>Ore de Utilizare Unelte</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {tools.map((tool) => (
              <div key={tool.id} className="glass-row-hover flex items-center justify-between p-3 bg-white/30 backdrop-blur-sm rounded-xl border border-white/40">
                <div>
                  <p className="font-semibold">{tool.name}</p>
                  {tool.description && <p className="text-sm text-slate-600">{tool.description}</p>}
                </div>
                <div className="text-right">
                  <p className="font-mono font-bold text-lg">{tool.total_hours?.toFixed(1) || 0}</p>
                  <p className="text-xs text-slate-500">ore totale</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Pipeline Timeline - Deviz → Contract → Proiect */}
      <ProjectPipelineTimeline companyId={companyId} />
      
      {/* Dashboard Statistics with Charts */}
      <DashboardStats companyId={companyId} workers={workers} sites={sites} />
    </div>
  );
}
